/**
 * EmergeExplorerPage — public read-only view of the EMERGE off-chain
 * ledger. Any user (signed-in or not) can look up a block height or
 * transaction hash to verify a BLK PACKS transaction. This is the
 * transparency layer for the whole ecosystem.
 *
 * Two routes:
 *   /emerge/block/:height  → single block + all its transactions
 *   /emerge/tx/:hash       → single transaction detail
 *
 * Backed by public endpoints in routes/verify.py — no auth headers
 * required so a court, a Stripe dispute handler, or a curious user
 * can all cross-reference the same data.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Shield, ExternalLink, Clock, Hash, ChevronLeft, Copy, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {/* clipboard blocked */ }
  };
  return (
    <button onClick={doCopy} className="ml-2 text-white/40 hover:text-white/80" title="Copy" data-testid="emerge-copy-btn">
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
};

const Mono = ({ children, "data-testid": testId }) => (
  <code className="font-mono text-xs text-white/80 bg-black/40 border border-white/10 rounded px-2 py-1 break-all" data-testid={testId}>{children}</code>
);

const TxRow = ({ tx }) => (
  <div className="rounded-xl bg-black/40 border border-white/10 p-3 text-xs space-y-1" data-testid={`emerge-tx-row-${tx.tx_hash}`}>
    <div className="flex items-center gap-2 text-white/60"><Hash size={11} /><Mono>{tx.tx_hash}</Mono><CopyBtn value={tx.tx_hash} /></div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-white/70">
      <span><b className="text-amber-300">Event:</b> {tx.event}</span>
      {tx.to_user?.username && <span><b className="text-white/40">→</b> @{tx.to_user.username}</span>}
      {tx.from_user?.username && <span><b className="text-white/40">←</b> @{tx.from_user.username}</span>}
      {tx.payload?.amount_usd && <span><b className="text-emerald-300">${tx.payload.amount_usd}</b></span>}
      {tx.payload?.points_awarded && <span>+{tx.payload.points_awarded} pts</span>}
      {tx.payload?.method && <span className="text-white/40 uppercase text-[10px]">{tx.payload.method}</span>}
    </div>
    <div className="text-white/40 text-[10px]"><Clock size={9} className="inline mr-1" />{tx.timestamp}</div>
  </div>
);

export const EmergeBlockPage = () => {
  const { height } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    axios.get(`${API}/verify/block/${height}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Block not found on EMERGE ledger"));
  }, [height]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 sm:p-8" data-testid="emerge-block-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-xs mb-6"><ChevronLeft size={14} />BLK PACKS</Link>
        <div className="flex items-center gap-2 mb-6">
          <Shield className="text-emerald-400" size={22} />
          <h1 className="text-2xl sm:text-3xl font-black">EMERGE Block <span className="text-emerald-400">#{height}</span></h1>
        </div>

        {err && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm">
            <p>{err}</p>
            <p className="mt-3 text-white/60">
              Looking up a serial number from a card or slab?{" "}
              <Link to={`/verify/${encodeURIComponent(height || "")}`} className="text-amber-300 font-bold hover:text-amber-200">Verify it here</Link>
              {" "}— or if you bought it on eBay/Amazon,{" "}
              <Link to="/claim" className="text-amber-300 font-bold hover:text-amber-200" data-testid="block-notfound-claim-link">submit a claim</Link>.
            </p>
          </div>
        )}
        {!err && !data && <p className="text-white/50 text-sm">Loading block…</p>}

        {data && (
          <>
            <div className="rounded-2xl bg-black/40 border border-white/10 p-4 sm:p-5 mb-6 text-sm space-y-2" data-testid="emerge-block-header">
              <div className="flex items-start gap-2"><span className="text-white/40 w-24 flex-shrink-0">Block Hash:</span><Mono data-testid="emerge-block-hash">{data.block_hash}</Mono><CopyBtn value={data.block_hash || ""} /></div>
              {data.prev_tx_hash && <div className="flex items-start gap-2"><span className="text-white/40 w-24 flex-shrink-0">Prev Hash:</span><Mono>{data.prev_tx_hash}</Mono></div>}
              <div className="flex items-start gap-2"><span className="text-white/40 w-24 flex-shrink-0">Timestamp:</span><span>{data.timestamp}</span></div>
              <div className="flex items-start gap-2"><span className="text-white/40 w-24 flex-shrink-0">Tx Count:</span><span className="text-amber-300 font-bold">{data.tx_count}</span></div>
            </div>

            <h2 className="text-sm font-black uppercase tracking-widest text-white/50 mb-3">Transactions in this block</h2>
            <div className="space-y-3">
              {(data.transactions || []).map((tx) => <TxRow key={tx.tx_hash} tx={tx} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const EmergeDropPage = () => {
  const { dropId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    axios.get(`${API}/verify/drop/${dropId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Drop not found on EMERGE ledger"));
  }, [dropId]);

  const notFound = data && data.status === "NOT_FOUND";
  const sealed = data && data.status === "SEALED";

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 sm:p-8" data-testid="emerge-drop-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-xs mb-6"><ChevronLeft size={14} />BLK PACKS</Link>
        <div className="flex items-center gap-2 mb-6">
          <Shield className="text-emerald-400" size={22} />
          <h1 className="text-2xl sm:text-3xl font-black">EMERGE Drop Ledger</h1>
        </div>

        {err && <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm" data-testid="emerge-drop-error">{err}</div>}
        {!err && !data && <p className="text-white/50 text-sm">Loading drop…</p>}
        {notFound && <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-white/70 text-sm" data-testid="emerge-drop-notfound">No record found on EMERGE for <span className="text-red-300">{dropId}</span></div>}

        {data && !notFound && (
          <>
            <div className="rounded-2xl bg-black/40 border border-white/10 p-4 sm:p-5 mb-6 text-sm space-y-3" data-testid="emerge-drop-header">
              <div className="flex items-start gap-4">
                {data.drop?.image_url && (
                  <img src={data.drop.image_url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white/40 text-[10px] uppercase tracking-[0.25em]">Drop</div>
                  <h2 className="text-lg sm:text-xl font-black text-white truncate" data-testid="emerge-drop-name">{data.drop?.name || "Untitled Drop"}</h2>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/60 mt-1">
                    {data.drop?.sport && <span className="uppercase">{data.drop.sport}</span>}
                    {data.drop?.series_number && <span>Series #{data.drop.series_number}</span>}
                    {typeof data.drop?.price === "number" && <span className="text-amber-300 font-bold">{data.drop.price} pts</span>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                <div className="bg-white/5 rounded-lg p-2 text-center" data-testid="emerge-drop-stat-events">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider">Events</div>
                  <div className="text-emerald-300 font-black text-base">{data.stats?.total_events ?? 0}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider">Mints</div>
                  <div className="text-white font-black text-base">{data.stats?.mints ?? 0}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider">Rips</div>
                  <div className="text-amber-300 font-black text-base">{data.stats?.rips ?? 0}</div>
                </div>
                <div className="bg-white/5 rounded-lg p-2 text-center">
                  <div className="text-[9px] text-white/40 uppercase tracking-wider">Owners</div>
                  <div className="text-white font-black text-base">{data.stats?.unique_owners ?? 0}</div>
                </div>
              </div>
              {data.first_block && data.latest_block && (
                <div className="text-[10px] text-white/40 pt-1">
                  Ledger footprint: block <Link to={`/emerge/block/${data.first_block}`} className="text-emerald-300 hover:underline font-mono">#{data.first_block}</Link>
                  {" "}→ block <Link to={`/emerge/block/${data.latest_block}`} className="text-emerald-300 hover:underline font-mono">#{data.latest_block}</Link>
                </div>
              )}
            </div>

            {sealed ? (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm" data-testid="emerge-drop-sealed">
                This drop hasn't produced any on-chain events yet. Once packs are minted or ripped, this ledger will fill up.
              </div>
            ) : (
              <>
                <h2 className="text-sm font-black uppercase tracking-widest text-white/50 mb-3">Recent EMERGE events</h2>
                <div className="space-y-3" data-testid="emerge-drop-events">
                  {(data.recent_events || []).map((tx) => <TxRow key={tx.tx_hash} tx={tx} />)}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const EmergeTxPage = () => {
  const { hash } = useParams();
  const [tx, setTx] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setTx(null); setErr(null);
    axios.get(`${API}/verify/tx/${hash}`)
      .then((r) => setTx(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Transaction not found on EMERGE ledger"));
  }, [hash]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 sm:p-8" data-testid="emerge-tx-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-xs mb-6"><ChevronLeft size={14} />BLK PACKS</Link>
        <div className="flex items-center gap-2 mb-6">
          <Shield className="text-emerald-400" size={22} />
          <h1 className="text-2xl sm:text-3xl font-black">EMERGE Transaction</h1>
        </div>

        {err && <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm">{err}</div>}
        {!err && !tx && <p className="text-white/50 text-sm">Loading transaction…</p>}

        {tx && (
          <div className="rounded-2xl bg-black/40 border border-white/10 p-4 sm:p-5 space-y-3 text-sm">
            <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">Tx Hash:</span><Mono data-testid="emerge-tx-hash-value">{tx.tx_hash}</Mono><CopyBtn value={tx.tx_hash || ""} /></div>
            <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">Block:</span>
              <Link to={`/emerge/block/${tx.block_height}`} className="text-emerald-300 hover:underline font-mono font-bold" data-testid="emerge-tx-block-link">#{tx.block_height}</Link>
              <ExternalLink size={11} className="text-emerald-300/60" />
            </div>
            {tx.prev_tx_hash && <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">Prev Hash:</span><Mono>{tx.prev_tx_hash}</Mono></div>}
            <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">Event:</span><span className="text-amber-300 font-black">{tx.event}</span></div>
            <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">Timestamp:</span><span>{tx.timestamp}</span></div>
            {tx.from_user?.username && <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">From:</span><span>@{tx.from_user.username}</span></div>}
            {tx.to_user?.username && <div className="flex items-start gap-2"><span className="text-white/40 w-28 flex-shrink-0">To:</span><span>@{tx.to_user.username}</span></div>}
            <div>
              <p className="text-white/40 mb-1">Payload:</p>
              <pre className="bg-black/60 border border-white/10 rounded p-3 text-[11px] overflow-x-auto whitespace-pre-wrap">{JSON.stringify(tx.payload || {}, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmergeBlockPage;
